import Swiper from '~/ui/components/common/Swiper/Swiper';
import { Loader } from '@Hotels-at-Home/hah-fe-core/dist/components';
import { useEffect, useState, forwardRef } from 'react';
import { useItemListName } from '@Hotels-at-Home/hah-fe-core/dist/hooks';
import { dataLayerFeaturedProductsAccor } from '@Hotels-at-Home/hah-fe-core/dist/utils';
import { getPrices } from '@Hotels-at-Home/hah-fe-core/dist/utils/price';
import { getProductLinkFromUrlRewrites } from '@Hotels-at-Home/hah-fe-core/dist/utils/product';
import { accorTagging, siteName } from '~/constants/siteConfig';
import { useRouter } from 'next/router';
import { SwiperRef } from 'swiper/react';
import { SwiperOptions } from 'swiper/types';

import { IYmalItem } from '@Hotels-at-Home/hah-fe-core/dist/types/ymal';
import { useSelector } from '~/store/hooks';
import { selectYmals } from '@Hotels-at-Home/hah-fe-core/dist/storeSelectors/ymal';
import HomeSlide from '~/ui/pages/Home/common/HomeSlide';
import { TSlide } from './types';

interface IProps extends SwiperOptions {
  ymalName: string;
  sliceArray?: number;
  sliceBeginning?: number;
  isHomepageList?: boolean;
  className?: string;
  slideClassName?: (slide: any) => string;
  navigation?: boolean;
  pagination?: boolean;
  arrowBorder?: boolean;
  blackBackground?: boolean;
  hasMobileImage?: boolean;
  imgLoading?: 'eager' | 'lazy';
  hasHoverCTA?: boolean;
}

const CommonSwiper = forwardRef<SwiperRef, IProps>(
  (
    {
      ymalName,
      sliceArray,
      sliceBeginning = 0,
      isHomepageList = false,
      className,
      slideClassName,
      navigation,
      pagination,
      arrowBorder,
      blackBackground,
      hasMobileImage,
      imgLoading = 'lazy',
      hasHoverCTA = false,
      ...swiperProps
    },
    ref,
  ) => {
    const [isYmalLoading, setIsYmalLoading] = useState(true);
    const [ymalResponse, setYmalResponse] = useState<IYmalItem[]>(null);
    const itemListName = useItemListName();
    const [swiperKey, setSwiperKey] = useState(0);
    const ymals = useSelector(selectYmals);
    const { locale } = useRouter();

    useEffect(() => {
      if (ymals.ymals.length > 0) {
        setYmalResponse(ymals.ymals);
        setIsYmalLoading(false);
      }
    }, [ymals]);

    useEffect(() => {
      if (!isHomepageList) {
        return;
      }
      if (accorTagging.accorTaggingEnabled) {
        try {
          if (!ymalResponse || !ymalResponse.length) return;

          const ymalList = ymalResponse.find(
            ymalItem => ymalItem.name === ymalName,
          ).related_products;
          dataLayerFeaturedProductsAccor({
            brand: siteName,
            items: ymalList,
            itemListName,
          });
        } catch (error) {
          console.error(error);
        }
      }
    }, [ymalResponse]);

    const cartYmal = ymalResponse?.find(ymalItem => ymalItem.name === ymalName);
    let ymalArray = cartYmal ? cartYmal.related_products : [];
    if (sliceArray) {
      ymalArray = ymalArray.slice(sliceBeginning, sliceArray);
    }

    const slides: TSlide[] = ymalArray.map((item, idx) => {
      const imgUrl = item.url_key
        ? `/images/homePage/ymalReplacements/${item.url_key}.webp`
        : item.thumbnail.url;
      const getMobileImageUrl = () => {
        if (!hasMobileImage) return undefined;
        if (item.url_key) return `/images/homePage/ymalReplacements/${item.url_key}-mob.webp`;
        return imgUrl;
      };
      const mobileImgUrl = getMobileImageUrl();

      return {
        name: item.name,
        image: imgUrl,
        image_mobile: hasMobileImage ? mobileImgUrl : undefined,
        prices: getPrices(item.price_range, locale),
        link: getProductLinkFromUrlRewrites(item.url_rewrites, locale),
        id: item.name,
        index: idx,
        product: item,
        short_description: item.short_description ? item.short_description.html : '',
        imgLoading,
        hasHoverCTA,
      };
    });

    useEffect(() => {
      const mediaQuery = window.matchMedia('(max-width: 768px)');
      const handleResize = () => {
        setSwiperKey(prevKey => prevKey + 1);
      };

      mediaQuery.addEventListener('change', handleResize);

      return () => {
        mediaQuery.removeEventListener('change', handleResize);
      };
    }, []);

    return isYmalLoading ? (
      <Loader />
    ) : (
      <Swiper
        key={swiperKey}
        slides={slides}
        renderSlide={slide => HomeSlide(slide)}
        className={className}
        slideClassName={slideClassName}
        navigation
        pagination
        arrowBorder={arrowBorder}
        blackBackground={blackBackground}
        {...swiperProps}
      />
    );
  },
);

CommonSwiper.displayName = 'CommonSwiper';

export default CommonSwiper;
